import { Component, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EventService } from '../../../service/event.service';
import { SwalService } from '../../../service/swal.service';
import { EventDetailStore } from '../event-detail.store';
import { IconComponent } from '../../../components/icon/icon.component';
import { shareOrCopy, todayIso } from '../../../utility/share';
import { clientNotes } from '../../../utility/quote';

type PaymentPart = 'deposit' | 'balance';

@Component({
  selector: 'app-pagamenti-tab',
  templateUrl: './pagamenti-tab.component.html',
  styleUrls: ['./pagamenti-tab.component.css'],
  imports: [CommonModule, FormsModule, TranslatePipe, IconComponent],
  providers: [DatePipe],
})
export class PagamentiTabComponent implements OnInit {
  readonly methods = ['cash', 'transfer', 'card', 'other'];
  savingQuick = false;

  constructor(
    public store: EventDetailStore,
    private ms: EventService,
    private swal: SwalService,
    private translate: TranslateService,
    private datePipe: DatePipe,
  ) {}

  ngOnInit(): void {
    if (!this.event.financial) this.event.financial = {};
    if (!this.event.financial.deposit) this.event.financial.deposit = {};
    if (!this.event.financial.balance) this.event.financial.balance = {};
  }

  get event(): any { return this.store.event; }
  get editMode(): boolean { return this.store.editMode; }
  get deposit(): any { return this.event.financial.deposit; }
  get balance(): any { return this.event.financial.balance; }
  get totalServices(): number { return this.store.totalServices; }
  get testCost(): number { return this.store.testCost; }
  get travelFee(): number { return this.store.travelFee; }
  get totalAll(): number { return this.store.totalAll; }
  get discountEuro(): number { return this.store.discountEuro; }
  get saldo(): number { return this.store.saldo; }
  get incassato(): number { return this.store.incassato; }
  get daIncassare(): number { return this.store.daIncassare; }
  get depositCost(): number { return this.store.depositCost; }
  get balanceOverdue(): boolean { return this.store.balanceOverdue; }

  setDepositType(type: 'fixed' | 'percent'): void {
    this.deposit.type = type;
  }

  get incassatoPct(): number {
    const t = this.totalAll;
    if (t <= 0) return 0;
    return Math.min(100, Math.round((this.incassato / t) * 100));
  }

  get isSaldato(): boolean {
    return this.totalAll > 0 && this.daIncassare <= 0;
  }

  methodLabel(method: string): string {
    return method ? this.translate.instant(`riepilogo.payments.methods.${method}`) : '';
  }

  // L'interruttore "Incassato" salva subito: è l'azione più frequente e non
  // deve dipendere dalla modalità modifica.
  togglePaid(kind: PaymentPart): void {
    const eventId = this.store.eventId;
    if (!eventId) return;
    const part = this.event.financial[kind];
    const prev = { paid: part.paid, paid_at: part.paid_at };
    part.paid = !part.paid;
    part.paid_at = part.paid ? part.paid_at || todayIso() : null;

    this.savingQuick = true;
    this.ms.updateFinancial(
      eventId,
      { [kind]: { paid: part.paid, paid_at: part.paid_at } },
      () => {
        this.savingQuick = false;
        this.swal.success(this.translate.instant('riepilogo.payments.saved'));
      },
      () => {
        this.savingQuick = false;
        part.paid = prev.paid;
        part.paid_at = prev.paid_at;
        this.swal.errorDialog(
          this.translate.instant('swal.error'),
          this.translate.instant('riepilogo.payments.save_error'),
        );
      },
    );
  }

  async shareSummary(): Promise<void> {
    const t = (key: string, params?: any) => this.translate.instant(key, params);
    const euro = (v: number) => `€ ${v.toFixed(2).replace('.', ',')}`;
    const state = (paid: boolean) => t(paid ? 'riepilogo.payments.state_received' : 'riepilogo.payments.state_pending');
    const due = !this.balance.paid && this.balance.due_date
      ? ` · ${t('riepilogo.payments.due_on')} ${this.datePipe.transform(this.balance.due_date, 'dd/MM/yyyy')}`
      : '';

    const lines = [
      `${t('riepilogo.payments.title')} · ${this.store.clientFullName}`,
      `${t('riepilogo.payments.total')}: ${euro(this.totalAll)}`,
      `${t('riepilogo.payments.deposit')}: ${euro(this.depositCost)} (${state(!!this.deposit.paid)})`,
      `${t('riepilogo.payments.balance')}: ${euro(this.saldo)} (${state(!!this.balance.paid)})${due}`,
    ];

    const notes = clientNotes(this.event);
    if (notes) lines.push('', `${t('quote.page.notes')}: ${notes}`);

    const outcome = await shareOrCopy({ text: lines.join('\n') });
    if (outcome === 'copied') this.swal.success(t('riepilogo.hero.copied'));
  }
}
